<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import type { ReportColumn } from '../../models/column.types'
import type { ReportFilter } from '../../models/filter.types'
import type {
  ReportTableCustomization,
  ReportTableSavedViewDraft,
  ReportTableType,
} from '../../models/tablePresentation.types'
import {
  customizeGroups,
  customizeMetricGroups,
  type CustomizeGroupKey,
  type CustomizeMetricGroupKey,
} from '../../service/customizeGroups'

import CustomizeColumnList from './CustomizeColumnList.vue'
import CustomizeMetricGroups from './CustomizeMetricGroups.vue'

import { useI18n } from '@/shared/i18n'
import { Button, Icon, Input, Modal } from '@/shared/ui'

const open = defineModel<boolean>({ default: false })
const props = withDefaults(
  defineProps<{
    columns: readonly ReportColumn[]
    visibleFields?: readonly string[]
    frozenFields?: readonly string[]
    expandedFields?: readonly string[]
    expandableFields?: readonly string[]
    requiredFields?: readonly string[]
    tableType?: ReportTableType
    breakdownField?: string | null
    filters?: readonly ReportFilter[]
    /** Off for surfaces with no named views (e.g. Summary's creative drawer). */
    allowSaveView?: boolean
    /** Off when a host container (a `Drawer`) already frames and pads the panel. */
    framed?: boolean
    totalRows?: readonly ('other' | 'ga4')[]
    ga4TotalAvailable?: boolean
  }>(),
  {
    visibleFields: () => [],
    expandedFields: () => [],
    expandableFields: () => [],
    requiredFields: () => [],
    tableType: 'table',
    breakdownField: null,
    filters: () => [],
    allowSaveView: true,
    framed: true,
    totalRows: () => ['other', 'ga4'],
    ga4TotalAvailable: false,
  },
)

const emit = defineEmits<{
  apply: [settings: ReportTableCustomization]
  'save-view': [view: ReportTableSavedViewDraft]
}>()

const { t } = useI18n()
const orderedFields = ref<string[]>([])
const visible = ref(new Set<string>())
const expanded = ref(new Set<string>())
const totalRowSelection = ref(new Set<'other' | 'ga4'>())
const viewName = ref('')
const attemptedSave = ref(false)
const saveModalOpen = ref(false)
const search = ref('')
const expandedGroups = ref(new Set<string>())
const columnsByField = computed(
  () => new Map(props.columns.map((column) => [column.field, column] as const)),
)
const orderedColumns = computed(() =>
  orderedFields.value.flatMap((field) => {
    const column = columnsByField.value.get(field)
    return column ? [column] : []
  }),
)
const resolvedFrozen = computed(
  () =>
    new Set(
      props.frozenFields ??
        props.columns.filter((column) => column.frozen).map((column) => column.field),
    ),
)
/** Static `t()` calls so `@intlify/vue-i18n/no-unused-keys` can see each heading key. */
function groupLabel(key: CustomizeGroupKey): string {
  if (key === 'dimensions') return t('reports.table.dimensions')
  if (key === 'labels') return t('reports.table.labels')
  return t('reports.table.metrics')
}

const filteredColumns = computed(() => {
  const query = search.value.trim().toLocaleLowerCase()
  return query.length === 0
    ? orderedColumns.value
    : orderedColumns.value.filter((column) => column.header.toLocaleLowerCase().includes(query))
})
const columnGroups = computed(() =>
  customizeGroups(filteredColumns.value, props.breakdownField).map((group) => ({
    ...group,
    label: groupLabel(group.key),
  })),
)
const metricSections = computed(() => {
  const metrics = columnGroups.value.find((group) => group.key === 'metrics')?.columns ?? []
  return customizeMetricGroups(metrics).map((group) => ({
    ...group,
    label: metricGroupLabel(group.key),
  }))
})
const nameError = computed(() => attemptedSave.value && viewName.value.trim().length === 0)

function metricGroupLabel(key: CustomizeMetricGroupKey): string {
  if (key === 'ga4') return t('reports.table.metricGroups.ga4')
  if (key === 'hardal') return t('reports.table.metricGroups.hardal')
  if (key === 'mail') return t('reports.table.metricGroups.mail')
  if (key === 'firstParty') return t('reports.table.metricGroups.firstParty')
  if (key === 'attribution') return t('reports.table.metricGroups.attribution')
  if (key === 'adapty') return t('reports.table.metricGroups.adapty')
  if (key === 'adjust') return t('reports.table.metricGroups.adjust')
  if (key === 'appsflyer') return t('reports.table.metricGroups.appsflyer')
  return t('reports.table.metricGroups.adPerformance')
}

watch(
  open,
  (isOpen) => {
    if (!isOpen) return
    orderedFields.value = props.columns.map((column) => column.field)
    visible.value = new Set(
      props.visibleFields.length > 0
        ? props.visibleFields
        : props.columns.filter((column) => !column.invisible).map((column) => column.field),
    )
    expanded.value = new Set(props.expandedFields)
    totalRowSelection.value = new Set(props.totalRows)
    for (const field of props.requiredFields) visible.value.add(field)
    attemptedSave.value = false
    saveModalOpen.value = false
    search.value = ''
    expandedGroups.value = new Set([
      'dimensions',
      'labels',
      'metrics',
      ...customizeMetricGroups(props.columns.filter((column) => column.numeric)).map(
        (group) => `metric:${group.key}`,
      ),
    ])
  },
  { immediate: true },
)

function setVisible(field: string, value: boolean): void {
  const next = new Set(visible.value)
  if (value || props.requiredFields.includes(field)) next.add(field)
  else next.delete(field)
  visible.value = next
  applyCurrent()
}

function setGroupVisible(columns: readonly ReportColumn[], value: boolean): void {
  const next = new Set(visible.value)
  for (const column of columns) {
    if (value || props.requiredFields.includes(column.field)) next.add(column.field)
    else next.delete(column.field)
  }
  visible.value = next
  applyCurrent()
}

function isGroupExpanded(key: string): boolean {
  return expandedGroups.value.has(key)
}

function toggleExpanded(key: string): void {
  const next = new Set(expandedGroups.value)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  expandedGroups.value = next
}

function toggleTotalRow(kind: 'other' | 'ga4'): void {
  const next = new Set(totalRowSelection.value)
  if (next.has(kind)) next.delete(kind)
  else next.add(kind)
  totalRowSelection.value = next
  applyCurrent()
}

function setExpanded(field: string, value: boolean): void {
  const next = new Set(expanded.value)
  if (value) next.add(field)
  else next.delete(field)
  expanded.value = next
  applyCurrent()
}

function customization(): ReportTableCustomization {
  const visibleFields = orderedFields.value.filter((field) => visible.value.has(field))
  return {
    visibleFields,
    columnOrder: [...orderedFields.value],
    frozenFields: orderedFields.value.filter(
      (field) => visible.value.has(field) && resolvedFrozen.value.has(field),
    ),
    expandedFields: orderedFields.value.filter(
      (field) => visible.value.has(field) && expanded.value.has(field),
    ),
    tableType: props.tableType,
    breakdownField: props.breakdownField,
    totalRows: [...totalRowSelection.value],
  }
}

function cloneFilter(filter: ReportFilter): ReportFilter {
  return {
    ...filter,
    value: Array.isArray(filter.value) ? [...filter.value] : filter.value,
  }
}

function applyCurrent(): void {
  emit('apply', customization())
}

function openSaveModal(): void {
  viewName.value = ''
  attemptedSave.value = false
  saveModalOpen.value = true
}

function saveView(): void {
  attemptedSave.value = true
  const name = viewName.value.trim()
  if (!name) return
  emit('save-view', {
    ...customization(),
    name,
    filters: props.filters.map(cloneFilter),
  })
  saveModalOpen.value = false
  attemptedSave.value = false
}
</script>

<template>
  <aside
    v-if="open"
    class="report-table-customize flex h-full min-h-0 max-h-full flex-col overflow-hidden bg-surface"
    :class="{ 'border border-border rounded-md': framed }"
    :aria-label="t('reports.table.customizeTitle')"
  >
    <div class="report-table-customize__totals px-md py-sm border-b border-border">
      <h3 class="m-0 text-sm font-semibold">{{ t('reports.table.totalRows') }}</h3>
      <label class="mt-xs flex items-center gap-xs text-sm">
        <input
          type="checkbox"
          :checked="totalRowSelection.has('other')"
          @change="toggleTotalRow('other')"
        />
        {{ t('reports.table.otherTotal') }}
      </label>
      <label class="mt-xs flex items-center gap-xs text-sm">
        <input
          type="checkbox"
          :checked="totalRowSelection.has('ga4')"
          :disabled="!ga4TotalAvailable"
          @change="toggleTotalRow('ga4')"
        />
        {{ t('reports.table.ga4Total') }}
      </label>
    </div>
    <div
      class="report-table-customize__search mt-2 flex flex-none items-start gap-sm pb-sm"
      :class="{ 'px-md pt-md': framed }"
    >
      <Input
        v-model="search"
        class="min-w-0 flex-1"
        type="search"
        :label="t('common.search')"
        :placeholder="t('reports.table.customizeSearchPlaceholder')"
      >
        <template #start>
          <Icon name="search" :size="14" />
        </template>
      </Input>
      <Button
        v-if="allowSaveView"
        class="flex-none"
        variant="outline"
        density="compact"
        @click="openSaveModal"
      >
        <template #start
          ><Icon name="bookmark" weight="light" :size="16" aria-hidden="true"
        /></template>
        {{ t('reports.table.saveView') }}
      </Button>
    </div>
    <div
      class="report-table-customize__body min-h-0 flex-1 overflow-y-auto"
      :class="{ 'px-md pb-md': framed }"
    >
      <section
        v-for="group in columnGroups"
        :key="group.key"
        class="report-table-customize__group border-b border-border py-sm"
      >
        <div class="report-table-customize__group-heading flex items-center">
          <h3 class="m-0 min-w-0 flex-1">
            <button
              type="button"
              class="flex w-full items-center justify-between gap-sm border-0 bg-transparent p-0 text-start text-sm font-semibold text-primary cursor-pointer"
              :aria-expanded="isGroupExpanded(group.key)"
              :aria-label="
                isGroupExpanded(group.key)
                  ? t('reports.table.collapseGroup', { group: group.label })
                  : t('reports.table.expandGroup', { group: group.label })
              "
              @click="toggleExpanded(group.key)"
            >
              <span>{{ group.label }}</span>
              <Icon
                :name="isGroupExpanded(group.key) ? 'chevron-down' : 'chevron-up'"
                :size="12"
                aria-hidden="true"
              />
            </button>
          </h3>
        </div>

        <CustomizeColumnList
          v-if="group.key !== 'metrics' && isGroupExpanded(group.key)"
          :columns="group.columns"
          :visible-fields="visible"
          :expanded-fields="expanded"
          :expandable-fields="expandableFields"
          :required-fields="requiredFields"
          @visible="setVisible"
          @expanded="setExpanded"
        />

        <CustomizeMetricGroups
          v-else-if="group.key === 'metrics' && isGroupExpanded(group.key)"
          :groups="metricSections"
          :visible-fields="visible"
          :expanded-fields="expanded"
          :open-groups="expandedGroups"
          :expandable-fields="expandableFields"
          :required-fields="requiredFields"
          @group-visible="setGroupVisible"
          @toggle-group="toggleExpanded"
          @visible="setVisible"
          @expanded="setExpanded"
        />
      </section>
    </div>

    <!--
      Nested here (not a sibling root) so `ReportTableCustomizePanel` stays a SINGLE-root
      template: `Modal` teleports its own content to the overlay layer (`FloatingLayer` inside
      it), so nesting it changes nothing about where it visually renders — but a second root
      node here would stop Vue from auto-forwarding the caller's `class`/`style` (the height sync
      to the table's own measured height, `DataVisible.vue`) onto the `<aside>` at all.
    -->
    <Modal
      v-if="allowSaveView"
      v-model="saveModalOpen"
      size="sm"
      :title="t('reports.table.saveView')"
      :close-label="t('reports.actions.close')"
    >
      <Input
        v-model="viewName"
        :label="t('reports.table.viewName')"
        :placeholder="t('reports.table.viewNamePlaceholder')"
        :error="nameError"
        v-bind="nameError ? { errorMessage: t('reports.table.viewNameRequired') } : {}"
        @enter="saveView"
      />
      <template #buttons>
        <div class="flex justify-end gap-sm">
          <Button variant="outline" @click="saveModalOpen = false">{{ t('common.cancel') }}</Button>
          <Button variant="primary" @click="saveView">{{ t('reports.table.saveView') }}</Button>
        </div>
      </template>
    </Modal>
  </aside>
</template>
